import { useQuery } from "@tanstack/react-query";
import { Banner, Button, Dialog, Skeleton } from "@guestpilot/ui-kit";
import { useI18n } from "../i18n";
import { Markdown } from "./Markdown";

export type LegalDoc = "tos" | "privacy" | "dpa" | "retention" | "portability";

/**
 * The text of a legal document, from the public files the web app serves (`/legal/<language>/<doc>.md`, synced from
 * `_truth/legal` by `pnpm legal:sync`). A language without a translation shows the English text and says so (BR-234, BR-180).
 */
async function loadDoc(doc: LegalDoc, lang: string): Promise<{ text: string; lang: string }> {
  for (const l of lang === "en" ? ["en"] : [lang, "en"]) {
    const res = await fetch(`/legal/${l}/${doc}.md`);
    const type = res.headers.get("content-type") ?? "";
    if (res.ok && !type.includes("text/html")) return { text: await res.text(), lang: l };
  }
  throw new Error(`legal document ${doc} not available`);
}

export function LegalDocDialog({
  doc,
  title,
  version,
  onClose,
}: {
  doc: LegalDoc | null;
  title: string;
  version?: string;
  onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const q = useQuery({
    queryKey: ["legal", doc, locale],
    queryFn: () => loadDoc(doc as LegalDoc, locale),
    enabled: doc !== null,
    staleTime: Infinity,
  });
  return (
    <Dialog
      open={doc !== null}
      size="lg"
      title={version ? `${title} (${version})` : title}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t("common:close")}</Button>}
    >
      <div className="gp-doc-scroll">
        {q.isLoading ? <Skeleton rows={6} /> : null}
        {q.isError ? <Banner tone="danger">{t("ui:enroll.docMissing")}</Banner> : null}
        {q.data && q.data.lang !== locale ? (
          <Banner tone="info">{t("ui:enroll.docEnglishOnly")}</Banner>
        ) : null}
        {q.data ? <Markdown source={q.data.text} /> : null}
      </div>
    </Dialog>
  );
}
